/**
 * sse.ts — fetch + ReadableStream 기반 커스텀 SSE 클라이언트
 *
 * 브라우저 내장 EventSource는 GET만 지원하고 커스텀 헤더/쿠키 제어가 제한적이다.
 * AI 사진 생성 API는 POST + httpOnly 쿠키 인증이 필요하므로
 * fetch API의 ReadableStream을 사용하여 SSE를 직접 구현한다.
 * credentials: 'include'로 httpOnly 쿠키를 자동 전송한다.
 *
 * SSE 프로토콜:
 * - 각 이벤트는 "data: {JSON}\n\n" 형식
 * - ':'로 시작하는 줄은 주석(keep-alive ping 등)으로 무시
 * - "data: [DONE]"은 스트림 종료 신호
 *
 * 청크 경계 문제:
 * - 네트워크 청크가 SSE 메시지 중간에서 끊길 수 있음
 * - buffer에 불완전한 마지막 줄을 보관하여 다음 청크와 합침
 *
 * @example
 *   await streamFetch<AIEvent>(
 *     '/api/ai/generate',
 *     { method: 'POST', body: JSON.stringify({ prompt: '...' }), signal },
 *     {
 *       onEvent: (event) => console.log(event.status, event.imageUrl),
 *       onError: (err) => console.error(err),
 *       onDone: () => console.log('생성 완료'),
 *     }
 *   )
 */

/** SSE 스트림 이벤트 콜백 인터페이스 */
interface StreamCallbacks<T> {
  /** 파싱된 JSON 이벤트가 도착할 때마다 호출 */
  onEvent: (event: T) => void
  /** 네트워크 오류 또는 HTTP 에러 시 호출 */
  onError?: (error: Error) => void
  /** 스트림이 정상 종료([DONE] 수신 또는 스트림 끝)되면 호출 */
  onDone?: () => void
}

/** API 베이스 URL (환경변수에서 로딩) */
const API_BASE = import.meta.env.VITE_API_URL || ''

/**
 * SSE 스트림을 열어 이벤트를 콜백으로 전달한다.
 *
 * @param url - API 경로 (API_BASE가 앞에 자동 추가됨)
 * @param options.method - HTTP 메서드 (기본: 'GET')
 * @param options.body - 요청 본문 (POST 시 JSON 문자열)
 * @param options.signal - AbortController의 signal (취소용)
 * @param callbacks - 이벤트/에러/완료 콜백
 */
export async function streamFetch<T>(
  url: string,
  options: { method?: string; body?: BodyInit; signal?: AbortSignal },
  callbacks: StreamCallbacks<T>,
): Promise<void> {
  const { method = 'GET', body, signal } = options

  // SSE 스트림임을 서버에 알리는 Accept 헤더
  const headers: Record<string, string> = { Accept: 'text/event-stream' }

  // body가 JSON 문자열이면 Content-Type 설정
  if (body && typeof body === 'string') {
    headers['Content-Type'] = 'application/json'
  }

  // ── 1. fetch 요청 (httpOnly 쿠키를 credentials: 'include'로 자동 전송) ──
  let res: Response
  try {
    res = await fetch(`${API_BASE}${url}`, { method, headers, body, signal, credentials: 'include' })
  } catch (err) {
    // AbortError는 사용자가 의도적으로 취소한 것이므로 에러로 전파하지 않음
    if ((err as Error).name !== 'AbortError') {
      callbacks.onError?.(err as Error)
    }
    return
  }

  // ── 2. HTTP 에러 체크 (JSON error body에서 실제 메시지 추출) ──
  if (!res.ok) {
    let message = `SSE 요청 실패: ${res.status}`
    try {
      const body = await res.json()
      message = body?.error?.message || body?.error || message
    } catch { /* JSON 파싱 실패 시 기본 메시지 사용 */ }
    callbacks.onError?.(new Error(message))
    return
  }

  // ── 3. ReadableStream 미지원 시 text() 폴백 ──
  // 구형 WebView 등에서 res.body가 null일 수 있음
  // 이 경우 전체 응답을 한 번에 받아 SSE 이벤트를 일괄 파싱
  if (!res.body) {
    try {
      const text = await res.text()
      for (const line of text.split('\n')) {
        const trimmed = line.trim()
        if (!trimmed || trimmed.startsWith(':')) continue
        if (trimmed.startsWith('data:')) {
          const data = trimmed.slice(5).trim()
          if (data === '[DONE]') break
          try {
            callbacks.onEvent(JSON.parse(data) as T)
          } catch {
            // JSON 파싱 실패 시 해당 이벤트만 무시
          }
        }
      }
      callbacks.onDone?.()
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        callbacks.onError?.(err as Error)
      }
    }
    return
  }

  // ── 4. 스트림 읽기 루프 ──
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = '' // 불완전한 줄을 보관하는 버퍼

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      // 바이트 배열을 문자열로 디코딩 (stream: true로 멀티바이트 문자 처리)
      buffer += decoder.decode(value, { stream: true })

      // 줄 단위로 분리
      const lines = buffer.split('\n')
      // 마지막 줄은 불완전할 수 있으므로 버퍼에 보관
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()

        // 빈 줄이나 주석(':'으로 시작)은 건너뜀
        if (!trimmed || trimmed.startsWith(':')) continue

        // "data:" 접두사가 있는 줄만 처리
        if (trimmed.startsWith('data:')) {
          const data = trimmed.slice(5).trim()

          // [DONE] 신호 — 스트림 정상 종료
          if (data === '[DONE]') {
            callbacks.onDone?.()
            return
          }

          // JSON 파싱 후 onEvent 콜백 디스패치
          try {
            callbacks.onEvent(JSON.parse(data) as T)
          } catch {
            // JSON 파싱 실패 시 해당 이벤트만 무시하고 계속 진행
          }
        }
      }
    }
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      callbacks.onError?.(err as Error)
      return
    }
  }

  // 스트림이 자연스럽게 끝난 경우에도 onDone 호출
  callbacks.onDone?.()
}

export type { StreamCallbacks }
